import { eliteStylePresets } from '@/data/elite-presets';
import { fontStacks } from '@/data/font-stacks';
import { adjustColorBrightness, getRelativeLuminance } from '@/utils/color';

export function generateTokensCSS(settings: any): string {
  const cs =
    settings.useColorStack && settings.colorStack
      ? settings.colorStack
      : null;
  const modeKey = settings.darkMode ? "dark" : "light";
  const sem =
    cs && cs.semanticTokens && cs.semanticTokens[modeKey]
      ? cs.semanticTokens[modeKey]
      : null;

  let primaryColor =
    sem && sem.primaryBg
      ? sem.primaryBg
      : settings.primaryColor || "#134e4a";
  let accentColor =
    sem && sem.accentBg
      ? sem.accentBg
      : settings.accentColor || "#d97706";
  const brandPrimary =
    cs && cs.primary && cs.primary[500] ? cs.primary[500] : primaryColor;
  const brandAccent =
    cs && cs.accent && cs.accent[500] ? cs.accent[500] : accentColor;

  const presetKey = settings.stylePreset || "signature";
  const preset =
    eliteStylePresets[presetKey]
      ? eliteStylePresets[presetKey]
      : eliteStylePresets.signature;

  const density = settings.designDensity || "balanced";
  const densityScale =
    density === "airy" ? 1.1 : density === "compact" ? 0.92 : 1.0;

  /* QA: L-1 — Enhanced density tokens */
  const sectionPy = density === 'airy' ? 'clamp(80px, 10vh, 140px)'
    : density === 'compact' ? 'clamp(40px, 5vh, 72px)'
    : 'clamp(56px, 8vh, 112px)';
  const cardPadding = density === 'airy' ? 'var(--sp-10)'
    : density === 'compact' ? 'var(--sp-5)' : 'var(--sp-8)';
  const gridGap = density === 'airy' ? 'var(--sp-10)'
    : density === 'compact' ? 'var(--sp-5)' : 'var(--sp-8)';

  // Color helpers (for premium ambient gradients)
  const hexToRgb = (hex: string): number[] => {
    if (!hex) return [0, 0, 0];
    const h = String(hex).replace("#", "").trim();
    const full =
      h.length === 3
        ? h
            .split("")
            .map((c) => c + c)
            .join("")
        : h;
    const n = parseInt(full, 16);
    if (Number.isNaN(n)) return [0, 0, 0];
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  };
  const rgbString = (hex: string): string => hexToRgb(hex).join(" ");

  // Calculate color variations
  const primaryLight = adjustColorBrightness(primaryColor, 20);
  const primaryDark = adjustColorBrightness(primaryColor, -20);
  const accentLight = adjustColorBrightness(accentColor, 20);
  const accentDark = adjustColorBrightness(accentColor, -20);
  /* QA: A-3 — Auto-detect button text color based on primary luminance */
  const onPrimary = getRelativeLuminance(primaryColor) > 0.179 ? '#1a1a1a' : '#ffffff';

  // Get font stack
  const fontStack = fontStacks[settings.fontStack] || fontStacks.system;
  const fontBody =
    fontStack.body || fontStack.value || fontStacks.system.value;
  const fontDisplay = fontStack.display || fontStack.value || fontBody;

  // Neutrals: choose preset or sane fallback for dark/light mismatch
  const isDark = !!settings.darkMode;
  const fallbackDark = {
    bg: "#0b0b0f",
    bgAlt: "#111118",
    text: "#f4f4f5",
    textLight: "#d4d4d8",
    textMuted: "#a1a1aa",
    border: "#27272a",
    borderLight: "#1f1f23",
  };
  const fallbackLight = {
    bg: "#faf9f7",
    bgAlt: "#f5f4f2",
    text: "#18181b",
    textLight: "#52525b",
    textMuted: "#a8a29e",
    border: "#e7e5e4",
    borderLight: "#f5f5f4",
  };

  const presetNeutrals = preset?.neutrals || fallbackLight;

  const presetShape = (preset as any)?.shape || {
    radiusSm: "4px",
    radiusMd: "10px",
    radiusLg: "14px",
    radiusXl: "18px",
  };

  // borderRadius override: 'none' → 0px, 'pill' → 9999px, 'rounded' → preset defaults
  const borderRadius = settings.borderRadius || 'rounded';
  const shape = borderRadius === 'none'
    ? { radiusSm: '0px', radiusMd: '0px', radiusLg: '0px', radiusXl: '0px' }
    : borderRadius === 'pill'
      ? { radiusSm: '9999px', radiusMd: '9999px', radiusLg: '9999px', radiusXl: '9999px' }
      : presetShape;

  // buttonRadius override: separate radius for buttons (falls back to borderRadius)
  const buttonRadius = settings.buttonRadius ?? settings.borderRadius ?? 'rounded';
  const btnShape = buttonRadius === 'none'
    ? { radiusSm: '0px', radiusMd: '0px', radiusLg: '0px', radiusXl: '0px' }
    : buttonRadius === 'pill'
      ? { radiusSm: '9999px', radiusMd: '9999px', radiusLg: '9999px', radiusXl: '9999px' }
      : presetShape;

  // Button Style tokens: filled | outline | soft
  const buttonStyle = settings.buttonStyle || 'filled';
  const btnTokens = (() => {
    switch (buttonStyle) {
      case 'outline':
        return {
          bg: 'transparent',
          color: primaryColor,
          border: primaryColor,
          hoverBg: `rgba(${rgbString(primaryColor)} / 0.08)`,
          hoverBorder: primaryLight,
          hoverColor: primaryLight,
        };
      case 'soft':
        return {
          bg: `rgba(${rgbString(primaryColor)} / 0.1)`,
          color: primaryColor,
          border: 'transparent',
          hoverBg: `rgba(${rgbString(primaryColor)} / 0.18)`,
          hoverBorder: 'transparent',
          hoverColor: primaryDark,
        };
      default: // 'filled'
        return {
          bg: primaryColor,
          color: onPrimary,
          border: primaryColor,
          hoverBg: primaryLight,
          hoverBorder: primaryLight,
          hoverColor: onPrimary,
        };
    }
  })();

  const neutrals = (() => {
    if (sem) {
      return {
        bg: sem.bg,
        bgAlt: sem.bgAlt || sem.bg,
        text: sem.text,
        textLight: sem.textMuted || sem.text,
        textMuted: sem.textMuted || sem.text,
        border:
          sem.border || (isDark ? "rgba(255, 255, 255, 0.1)" : "#e7e5e4"),
        borderLight:
          sem.borderLight ||
          sem.border ||
          (isDark ? "rgba(255, 255, 255, 0.05)" : "#f5f5f4"),
      };
    }
    if (isDark) {
      // If a light preset is used with dark mode, switch to a high-end dark neutral scheme
      if (presetKey !== "luxe" && presetKey !== "glass")
        return fallbackDark;
      return presetNeutrals;
    }
    // If a dark preset is used with light mode, keep readability by switching to light neutrals
    if (presetKey === "luxe" || presetKey === "glass")
      return fallbackLight;
    return presetNeutrals;
  })();

  const ambientOpacity = (preset as any)?.fx?.ambientOpacity ?? 0.85;
  const frame = (preset as any)?.frame || {
    edgeWidth: "0px",
    edgeA: "transparent",
    edgeB: "transparent",
  };

  return `/* ============================================
   DESIGN TOKENS
   Generated by Studio v5.0.0
   ============================================ */

:root {
  /* Preset Meta */
  --preset: "${presetKey}";
  --density: "${density}";
  --sp-scale: ${densityScale};

  /* Brand Colors */
  --c-brand-primary: ${brandPrimary};
  --c-brand-accent: ${brandAccent};
  --c-primary: ${primaryColor};
  --c-primary-light: ${primaryLight};
  --c-primary-dark: ${primaryDark};
  --c-accent: ${accentColor};
  --c-accent-light: ${accentLight};
  --c-accent-dark: ${accentDark};

  /* QA: A-3 — Accessible text on primary background */
  --c-on-primary: ${onPrimary};

  /* RGB for modern alpha colors */
  --c-primary-rgb: ${rgbString(primaryColor)};
  --c-accent-rgb: ${rgbString(accentColor)};

  /* Neutral Colors (Elite) */
  --c-white: #ffffff;
  --c-white-rgb: 255 255 255;
  --c-black: #000000;
  --c-black-rgb: 0 0 0;
  --c-bg: ${neutrals.bg};
  --c-bg-alt: ${neutrals.bgAlt};
  --c-text: ${neutrals.text};
  --c-text-light: ${neutrals.textLight};
  --c-text-muted: ${neutrals.textMuted};
  --c-border: ${neutrals.border};
  --c-border-light: ${neutrals.borderLight};

  /* Surfaces */
  --c-surface: rgba(var(--c-white-rgb, 255 255 255) / 0.75);
  --c-surface-strong: rgba(var(--c-white-rgb, 255 255 255) / 0.92);

  /* Effects */
  --fx-ambient-opacity: ${ambientOpacity};

  /* Signature Frame (optional) */
  --edge-width: ${frame.edgeWidth || "0px"};
  --edge-color-a: ${frame.edgeA || "transparent"};
  --edge-color-b: ${frame.edgeB || frame.edgeA || "transparent"};

  /* Typography */
  --font-body: ${fontBody};
  --font-display: ${fontDisplay};

  /* Font Sizes */
  --text-xs: clamp(calc(0.75rem * var(--sp-scale)), 1vw, calc(0.8rem * var(--sp-scale)));
  --text-sm: clamp(calc(0.875rem * var(--sp-scale)), 1.2vw, calc(0.95rem * var(--sp-scale)));
  --text-base: clamp(calc(1rem * var(--sp-scale)), 1.4vw, calc(1.05rem * var(--sp-scale)));
  --text-lg: clamp(calc(1.125rem * var(--sp-scale)), 1.8vw, calc(1.25rem * var(--sp-scale)));
  --text-xl: clamp(calc(1.25rem * var(--sp-scale)), 2vw, calc(1.5rem * var(--sp-scale)));
  --text-2xl: clamp(calc(1.5rem * var(--sp-scale)), 3vw, calc(2rem * var(--sp-scale)));
  --text-3xl: clamp(calc(1.875rem * var(--sp-scale)), 4vw, calc(2.5rem * var(--sp-scale)));
  --text-4xl: clamp(calc(2rem * var(--sp-scale)), 5vw, calc(3rem * var(--sp-scale)));
  --text-5xl: clamp(calc(2.5rem * var(--sp-scale)), 6vw, calc(3.5rem * var(--sp-scale)));

  --leading-tight: 1.15;
  --leading-snug: 1.3;
  --leading-normal: 1.5;
  --leading-body: 1.6;
  --leading-relaxed: 1.65;
  --tracking-tight: -0.02em;
  /* QA: D-3, D-5 — Extended typography tokens */
  --tracking-normal: 0em;
  --tracking-wide: 0.04em;
  --tracking-wider: 0.08em;
  --weight-normal: 400;
  --weight-medium: 500;
  --weight-semibold: 600;
  --weight-bold: 700;
  --weight-extrabold: 800;

  /* Spacing (4px baseline) */
  --sp-1: calc(0.25rem * var(--sp-scale));
  --sp-2: calc(0.5rem * var(--sp-scale));
  --sp-3: calc(0.75rem * var(--sp-scale));
  --sp-4: calc(1rem * var(--sp-scale));
  --sp-5: calc(1.25rem * var(--sp-scale));
  --sp-6: calc(1.5rem * var(--sp-scale));
  --sp-8: calc(2rem * var(--sp-scale));
  --sp-10: calc(2.5rem * var(--sp-scale));
  --sp-12: calc(3rem * var(--sp-scale));
  --sp-16: calc(4rem * var(--sp-scale));
  --sp-20: calc(5rem * var(--sp-scale));
  --sp-24: calc(6rem * var(--sp-scale));
  --sp-32: calc(8rem * var(--sp-scale));
  --sp-40: calc(10rem * var(--sp-scale));
  --sp-48: calc(12rem * var(--sp-scale));
  --sp-64: calc(16rem * var(--sp-scale));

  /* QA: VIS-1 — Upgraded multi-layer shadow system */
  --shadow-xs: 0 1px 2px rgba(0,0,0,0.04);
  --shadow-sm: 0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.04);
  --shadow-md: 0 4px 6px -1px rgba(0,0,0,0.07), 0 2px 4px -2px rgba(0,0,0,0.05);
  --shadow-lg: 0 10px 15px -3px rgba(0,0,0,0.08), 0 4px 6px -4px rgba(0,0,0,0.04);
  --shadow-xl: 0 20px 25px -5px rgba(0,0,0,0.08), 0 8px 10px -6px rgba(0,0,0,0.03);
  --shadow-2xl: 0 25px 50px -12px rgba(0,0,0,0.15);
  --shadow-inner: inset 0 2px 4px rgba(0,0,0,0.04);

  /* Border Radius */
  --radius-sm: ${shape.radiusSm};
  --radius-md: ${shape.radiusMd};
  --radius-lg: ${shape.radiusLg};
  --radius-xl: ${shape.radiusXl};
  --radius-full: 9999px;

  /* Transitions */
  --ease-out: cubic-bezier(0.16, 1, 0.3, 1);
  --ease-in: cubic-bezier(0.7, 0, 0.84, 0);
  --ease-in-out: cubic-bezier(0.45, 0, 0.55, 1);
  --ease-spring: cubic-bezier(0.34, 1.56, 0.64, 1);
  --duration-fast: 0.15s;
  --duration-normal: 0.25s;
  --duration-slow: 0.4s;
  --duration-slower: 0.6s;

  /* Container */
  --container-max: 1200px;
  --container-padding: clamp(var(--sp-4), 4vw, var(--sp-8));

  /* QA: L-1 — Density-driven layout tokens */
  --density-section-py: ${sectionPy};
  --density-card-p: ${cardPadding};
  --density-grid-gap: ${gridGap};

  /* Layout Grid System */
  --layout-columns-desktop: 12;
  --layout-columns-tablet: 8;
  --layout-columns-mobile: 4;
  --layout-gap: var(--density-grid-gap, var(--sp-8));
  --layout-max-width: 1200px;
  --layout-padding-inline: var(--sp-6);

  /* Breakpoints (documentation — CSS custom properties cannot be used in media queries)
     Desktop: min-width: 901px
     Tablet:  max-width: 900px  and  min-width: 641px
     Mobile:  max-width: 640px
  */
  --bp-desktop: 901px;
  --bp-tablet: 900px;
  --bp-mobile: 640px;

  /* Button Style — ${buttonStyle} */
  --btn-primary-bg: ${btnTokens.bg};
  --btn-primary-color: ${btnTokens.color};
  --btn-primary-border: ${btnTokens.border};
  --btn-primary-hover-bg: ${btnTokens.hoverBg};
  --btn-primary-hover-border: ${btnTokens.hoverBorder};
  --btn-primary-hover-color: ${btnTokens.hoverColor};

  /* Button Radius — ${buttonRadius} */
  --btn-radius: ${btnShape.radiusMd};

  /* Utility */
  --header-h: 44px;
}

/* Dark Mode helpers: keep tokens stable, but adjust surfaces */
.dark {
  --c-surface: rgba(17, 17, 24, 0.45);
  --c-surface-strong: rgba(17, 17, 24, 0.70);
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  :root {
    --duration-fast: 0.01ms;
    --duration-normal: 0.01ms;
    --duration-slow: 0.01ms;
  }
}`;
}
